import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { prepacksApi } from '@/services/api'
import { apiErrorMessage } from '@/utils/apiErrorMessage'
import { overriddenFields, resetTarget } from './prepackOverridesModel'

/**
 * The fields an operator changed on a pack (#1089), each with Reset, which
 * gives it back to the pack's schedule (or its template) and applies it now.
 * Renders nothing for a pack with none. `canReset` is the operator's
 * permission to change the pack; resetting its publishing can publish it, so
 * that also takes `canResetPublishing` (publish permission). `onReset(pack)`
 * gets the pack as saved.
 */
export default function PrepackOverrides({ slug, pack, canReset, canResetPublishing = false, onReset }) {
  const queryClient = useQueryClient()
  const fields = overriddenFields(pack)
  const resettable = (field) => canReset && (field !== 'status' || canResetPublishing)
  const resettableFields = fields.filter(({ field }) => resettable(field))
  const target = resetTarget(pack)
  const reset = useMutation({
    mutationFn: (field) => prepacksApi.resetPrepack(slug, pack.id, {
      fields: field ? [field] : resettableFields.map((item) => item.field),
      updated_at: pack.updated_at,
    }),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
      queryClient.invalidateQueries({ queryKey: ['prepacks', 'surfaces', slug] })
      onReset?.(saved)
      toast.success(`Reset to the ${target}`)
    },
    onError: (err) => {
      if (err?.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
        toast.error('This pack changed elsewhere. Reopen it and try again.')
        return
      }
      toast.error(apiErrorMessage(err, 'Failed to reset the pack'))
    },
  })
  if (fields.length === 0) return null
  return (
    <section aria-label="Edited on this pack" className="rounded-lg border border-[var(--prepacks-border)] p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold">Edited on this pack</span>
        {resettableFields.length > 1 ? (
          <button type="button" disabled={reset.isPending} onClick={() => reset.mutate(null)} className="text-xs text-[var(--prepacks-accent-text)] disabled:opacity-40">
            {`Reset all to ${target}`}
          </button>
        ) : null}
      </div>
      <ul className="mt-2 flex flex-col gap-1.5">
        {fields.map(({ field, label }) => (
          <li key={field} className="flex items-center justify-between gap-3 text-xs text-[var(--prepacks-text)]">
            <span>{label}</span>
            {resettable(field) ? (
              <button
                type="button"
                disabled={reset.isPending}
                onClick={() => reset.mutate(field)}
                aria-label={`Reset ${label.toLowerCase()} to ${target}`}
                className="flex items-center gap-1 text-[var(--prepacks-accent-text)] disabled:opacity-40"
              >
                <RotateCcw className="h-3 w-3" />
                {`Reset to ${target}`}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
